import { Divider, Form, Input, Select } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "../styles.css";
import ApiUsersManagement from "../../../../api/user-manager";
import useNotification from "../../../notification/notification";
import ModalWrapper from "../../../modal/modal";
import {
  selectIsSystemAdmin,
  selectIsAdmin,
} from "../../../../store/slices/user";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import UserAccessForm from "./user-access-form";
import { useProjectSites } from "./use-project-sites";
import { coversWholeProject, rowPayload, rowRoleOf, useAssignableRoles } from "./role-options";

const InviteUserModal = ({
  isOpen,
  close,
  scope = "organization",
  projectId = null,
}) => {
  const isSystemAdmin = useSelector(selectIsSystemAdmin);
  const isAdmin = useSelector(selectIsAdmin);
  const projects = useSelector((state) => state.projects.list || []);

  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [projectRoleDropdownKey, setProjectRoleDropdownKey] = useState(null);
  const [orgRole, setOrgRole] = useState(null);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);

  const [inProgress, setProgress] = useState(false);

  const [projectRows, setProjectRows] = useState([]);
  const [projectModalOpen, setProjectModalOpen] = useState(false);

  // LF360-2038: each row's sites come from its own project.
  const sitesFor = useProjectSites(
    projectRows.map((r) => r.projectId),
    isOpen
  );

  const siteIdsToSend = (row) =>
    sitesFor(row.projectId).sitesEnabled && !coversWholeProject(row)
      ? row.siteIds || []
      : [];

  const selectedProjectIds = projectRows
    .map((r) => r.projectId)
    .filter(Boolean);

  // LF360-2024: the roles the signed-in user may give — the server's answer,
  // custom roles included, and the same rule it checks.
  const roles = useAssignableRoles(isOpen);

  useEffect(() => {
    if (!isOpen || scope !== "organization") return;

    if (orgRole !== "user") {
      setProjectRows([]);
    }
  }, [orgRole, isOpen, scope]);

  useEffect(() => {
    if (!isOpen || scope !== "project" || !projectId) return;

    setOrgRole("user");
    setProjectRows([
      {
        key: `${projectId}-row`,
        projectId,
        ...rowRoleOf(null),
        siteIds: [],
      },
    ]);
  }, [isOpen, scope, projectId]);

  const onClose = () => {
    close();
    form.resetFields();
    setOrgRole(null);
    setRoleDropdownOpen(false);
    setProjectRows([]);
    setProjectRoleDropdownKey(null);
  };

  const getOrgRoleOptions = () => {
    const roles = []
    if (isSystemAdmin) {
      roles.push({ label: "System admin", value: "systemAdmin" });
    }
    if (isAdmin) {
      roles.push({ label: "Organization admin", value: "admin" });
    }
    roles.push({ label: "User", value: "user" });

    return roles;
  };

  const orgRoleOptions = getOrgRoleOptions();

  const updateRow = (key, patch) => {
    setProjectRows((rows) =>
      rows.map((row) =>
        row.key === key
          ? {
              ...row,
              ...patch,
              ...(coversWholeProject({ ...row, ...patch }) ? { siteIds: [] } : {}),
            }
          : row
      )
    );
  };

  const handleSelectProject = (project) => {
    if (!project || !project.id) return;

    setProjectRows((rows) => {
      if (rows.some((r) => r.projectId === project.id)) {
        return rows;
      }
      return [
        ...rows,
        {
          key: Date.now().toString() + Math.random().toString(16).slice(2),
          projectId: project.id,
          ...rowRoleOf(null),
          siteIds: [],
        },
      ];
    });
  };

  const inviteUser = () => {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      const { firstName, lastName, email } = values;

      setProgress(true);

      let roleToSend = orgRole;     // org-level role
      let projectsPayload = [];

      //
      // --- ORGANIZATION LEVEL ---
      //
      if (scope === "organization") {
        if (orgRole === "user") {
          projectsPayload = projectRows
            .filter(r => r.projectId && r.roleId)
            .map(r => rowPayload({ ...r, siteIds: siteIdsToSend(r) }));
        } else {
          projectsPayload = [];
        }
      }

      //
      // --- PROJECT LEVEL ---
      //
      if (scope === "project") {
        roleToSend = "user";

        projectsPayload = projectRows
          .filter(r => r.projectId && r.roleId)
          .map(r => rowPayload({ ...r, siteIds: siteIdsToSend(r) }));
      }

      ApiUsersManagement
        .invite({
          firstName,
          lastName,
          email,
          role: roleToSend,
          projects: projectsPayload,
        })
        .then(() => {
          openNotification({
            type: "success",
            message: (
              <p>
                {t("invite_user.notification.success", { email })}
              </p>
            ),
          });
        })
        .catch((e) => {
          openNotification({
            type: "error",
            message: (
              <p>
                {t(
                  `invite_user.notification.${
                    e?.response?.data?.error?.code || "error"
                  }`
                )}
              </p>
            ),
          });
        })
        .finally(() => {
          setProgress(false);
          onClose();
        });
    });
  };

  const getProjectTitle = (id) =>
    (projects || []).find((p) => p.id === id)?.title || "—";

  const currentProject =
    scope === "project" && projectId
      ? (projects || []).find((p) => p.id === projectId) || null
      : null;

  const modalTitle =
    scope === "project" && currentProject
      ? `${t("invite_user.title")} to ${currentProject.title}`
      : t("invite_user.title");

  const removeProjectRow = (key) => {
    setProjectRows((rows) => rows.filter((r) => r.key !== key));
  };

  return (
    <ModalWrapper
      width={720}
      isOpen={isOpen}
      title={modalTitle}
      onOk={inviteUser}
      onCancel={onClose}
      okText={t("invite_user.invite")}
      okButtonProps={{ size: "large" }}
      cancelButtonProps={{
        size: "large",
        style: { backgroundColor: "rgba(108, 108, 137, 0.08)" },
      }}
      confirmLoading={inProgress}
    >
      <Divider />

      <Form form={form} layout="vertical">

        <Form.Item name="firstName">
          <div className="invite-user__field">
            <p className="invite-user__field__name">
              {t("invite_user.firstName")}
            </p>
            <Input
              variant="borderless"
              placeholder={t("invite_user.placeholders.firstName")}
              maxLength={1000}
            />
          </div>
        </Form.Item>

        <Form.Item name="lastName">
          <div className="invite-user__field">
            <p className="invite-user__field__name">
              {t("invite_user.lastName")}
            </p>
            <Input
              variant="borderless"
              placeholder={t("invite_user.placeholders.lastName")}
              maxLength={1000}
            />
          </div>
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            {
              required: true,
              message: t("invite_user.validation.email_required"),
            },
            {
              type: "email",
              message: t("invite_user.validation.email_invalid"),
            },
          ]}
        >
          <div className="invite-user__field">
            <p className="invite-user__field__name">
              {t("invite_user.email")}
            </p>
            <Input
              variant="borderless"
              placeholder={t("invite_user.placeholders.email")}
              maxLength={1000}
            />
          </div>
        </Form.Item>

        {scope === "organization" && (
          <Form.Item name="role">
            <div className="invite-user__field">
              <p className="invite-user__field__name">
                {t("invite_user.role")}
              </p>

              <div className="invite-user__role-wrapper">
                <button
                  type="button"
                  className="invite-user__role-trigger"
                  onClick={() => setRoleDropdownOpen(true)}
                >
                  {orgRole ? (
                    <UserActivityStatus
                      userStatus={orgRole}
                      fontSize={14}
                      style={{ whiteSpace: "nowrap" }}
                    />
                  ) : (
                    <span className="invite-user__role-placeholder">
                      {t("invite_user.placeholders.role") || "Select role"}
                    </span>
                  )}
                  <span className="invite-user__role-arrow" />
                </button>

                <Select
                  className="invite-user__role-select"
                  variant="borderless"
                  value={orgRole}
                  open={roleDropdownOpen}
                  onOpenChange={(open) => setRoleDropdownOpen(open)}
                  onChange={(val) => {
                    setOrgRole(val);
                    form.setFieldsValue({ role: val });
                    setRoleDropdownOpen(false);
                  }}
                  options={orgRoleOptions}
                  popupMatchSelectWidth={false}
                />
              </div>
            </div>
          </Form.Item>
        )}

        <UserAccessForm
          scope={scope}
          orgRole={orgRole}
          sitesFor={sitesFor}
          projectRows={projectRows}
          updateRow={updateRow}
          roles={roles}
          projectRoleDropdownKey={projectRoleDropdownKey}
          setProjectRoleDropdownKey={setProjectRoleDropdownKey}
          getProjectTitle={getProjectTitle}
          projectModalOpen={projectModalOpen}
          setProjectModalOpen={setProjectModalOpen}
          selectedProjectIds={selectedProjectIds}
          onSelectProject={handleSelectProject}
          project={currentProject}
          removeProjectRow={removeProjectRow}
        />
      </Form>

      <Divider />
    </ModalWrapper>
  );
};

export default InviteUserModal;

